import { trimTrailingSlash } from "@shared/helpers/pathname";

/**
 * Registry of the detail views, and of what each part of their address means (CTX-773,
 * "Pestañas D1").
 *
 * A detail view has to be rebuildable from its URL alone: pasting it into a clean browser
 * tab, a reload, or a view restored later must paint the same screen as arriving from the
 * list. History state (`location.state`) does not travel with a URL, so nothing that a
 * view needs to paint may live there.
 *
 * Every query param a detail route reads falls in one of three classes:
 *
 * - **identity** — which resource this is. Two addresses with the same identity are the
 *   same resource, whatever else they carry. This is the only part that keys a view.
 * - **context** — the scope the resource is read in (`tenantId`). It travels with the URL
 *   so a partner looking at a child's resource keeps that scope across a reload, but it
 *   does not identify anything: resource ids are global, so the same connection seen with
 *   and without `tenantId` is still one connection.
 * - **state** — how the view is being looked at (sub-tab, chart range, latency
 *   destination). Changing it never creates or closes a view (intent I4).
 *
 * Params a route does not declare are carried along untouched and treated as state: they
 * can never widen the identity by accident.
 *
 * The two addressing conventions stay as they are (intent §2): Kontrol Edge identifies by
 * query param (`?portId=`), Reflector V2 by path param (`/details/:id`). A `:name` segment
 * in `path` is a path-param identity.
 */

export type DetailRouteKey =
  | "port"
  | "internet"
  | "cloudConnectivity"
  | "datacenter"
  | "backbone"
  | "sdwanHub"
  | "sdwanBranch"
  | "fwaas"
  | "extenderPopCpe"
  | "extenderPop"
  | "carrierBridge"
  | "bgpReflector"
  | "cluster";

export type DetailProduct = "kontrolEdge" | "reflectorV2";

export interface DetailRoute {
  key: DetailRouteKey;
  product: DetailProduct;
  /** As declared in the product's `routes.tsx`. */
  path: string;
  identity: readonly string[];
  context: readonly string[];
  state: readonly string[];
}

/** Written by `BandwidthChartUniversal` — see `chartRangeParams.ts`. */
export const CHART_RANGE_PARAMS = ["range", "interval", "from", "to"] as const;

/** The query param that carries a detail view's sub-tab, on every route that has one. */
export const DETAIL_TAB_PARAM = "tab";

/** The query param that carries the tenant scope a detail is read in. */
export const DETAIL_TENANT_PARAM = "tenantId";

export const DETAIL_ROUTES: readonly DetailRoute[] = [
  {
    key: "port",
    product: "kontrolEdge",
    path: "/kontrol/port-details",
    identity: ["portId"],
    context: [],
    // `subid` is a display hint some lists append; the port is the same without it.
    state: ["subid", ...CHART_RANGE_PARAMS],
  },
  {
    key: "internet",
    product: "kontrolEdge",
    path: "/kontrol/internet/internet-details",
    identity: ["connectionId"],
    context: [DETAIL_TENANT_PARAM],
    state: [...CHART_RANGE_PARAMS, "dest"],
  },
  {
    key: "cloudConnectivity",
    product: "kontrolEdge",
    path: "/kontrol/cloud-connectivity/details",
    identity: ["connectionId"],
    context: [DETAIL_TENANT_PARAM],
    state: [...CHART_RANGE_PARAMS],
  },
  {
    key: "datacenter",
    product: "kontrolEdge",
    path: "/kontrol/datacenter-details",
    identity: ["datacenterId"],
    context: [DETAIL_TENANT_PARAM],
    state: [...CHART_RANGE_PARAMS],
  },
  {
    key: "backbone",
    product: "kontrolEdge",
    path: "/kontrol/backbone-details",
    identity: ["backboneId"],
    context: [],
    state: [DETAIL_TAB_PARAM],
  },
  {
    key: "sdwanHub",
    product: "kontrolEdge",
    path: "/kontrol/sdwan-hub-details",
    identity: ["sdwanId"],
    context: [],
    state: [],
  },
  {
    key: "sdwanBranch",
    product: "kontrolEdge",
    path: "/kontrol/sdwan-branch-details",
    // A branch is only addressable under its hub; `getBackRoute` rebuilds the way back
    // from the same `sdwanId`.
    identity: ["sdwanId", "branchId"],
    context: [],
    state: [],
  },
  {
    key: "fwaas",
    product: "kontrolEdge",
    path: "/kontrol/fwaas-details",
    identity: ["fwaasId"],
    // Read in the SideNav lens, like its SD-WAN hub sibling and the list it comes from.
    context: [],
    // The part of the service selected on its map (CTX-1265); absent = the firewall.
    state: ["node"],
  },
  {
    key: "extenderPop",
    product: "kontrolEdge",
    path: "/kontrol/cpe-details",
    identity: ["extenderPopId"],
    context: [],
    state: [],
  },
  {
    // CTX-861. The bandwidth chart keeps its range in local state, so no
    // state params; the tenant rides along so a partner keeps the scope.
    key: "carrierBridge",
    product: "kontrolEdge",
    path: "/kontrol/carrier-bridge-details",
    identity: ["carrierBridgeId"],
    context: [DETAIL_TENANT_PARAM],
    state: [],
  },
  {
    key: "extenderPopCpe",
    product: "kontrolEdge",
    path: "/kontrol/cpe-device-details",
    // A CPE is read out of its site's detail, so it is addressed under it;
    // `getBackRoute` returns to that site, like an SD-WAN branch to its hub.
    identity: ["extenderPopId", "cpeId"],
    context: [],
    state: [],
  },
  {
    key: "bgpReflector",
    product: "reflectorV2",
    path: "/reflector-v2/management/bgp-reflectors/details/:id",
    identity: ["id"],
    context: [],
    state: [DETAIL_TAB_PARAM],
  },
  {
    key: "cluster",
    product: "reflectorV2",
    path: "/reflector-v2/management/clusters/details/:id",
    identity: ["id"],
    context: [],
    state: [DETAIL_TAB_PARAM],
  },
];

const ROUTES_BY_KEY = new Map(DETAIL_ROUTES.map((route) => [route.key, route]));

export function getDetailRoute(key: DetailRouteKey): DetailRoute {
  const route = ROUTES_BY_KEY.get(key);
  if (!route) throw new Error(`Unknown detail route: ${key}`);
  return route;
}

const PATH_PARAM = /^:(.+)$/;

/** Path-param values keyed by name, or null when `pathname` is not this route. */
function matchPath(route: DetailRoute, pathname: string): Record<string, string> | null {
  const wanted = route.path.split("/");
  const actual = trimTrailingSlash(pathname).split("/");
  if (wanted.length !== actual.length) return null;

  const params: Record<string, string> = {};
  for (let i = 0; i < wanted.length; i++) {
    const param = PATH_PARAM.exec(wanted[i]);
    if (param) {
      if (!actual[i]) return null;
      params[param[1]] = decodeURIComponent(actual[i]);
    } else if (wanted[i] !== actual[i]) {
      return null;
    }
  }
  return params;
}

export function matchDetailRoute(pathname: string): DetailRoute | null {
  return DETAIL_ROUTES.find((route) => matchPath(route, pathname) !== null) ?? null;
}

export interface DetailAddress {
  route: DetailRoute;
  identity: Record<string, string>;
  context: Record<string, string>;
  state: Record<string, string>;
}

/**
 * Splits a detail URL into its three classes. Null when the path is not a detail route,
 * or when the address lacks part of its identity — a detail URL without its id names no
 * resource.
 */
export function parseDetailAddress(pathname: string, search = ""): DetailAddress | null {
  for (const route of DETAIL_ROUTES) {
    const pathParams = matchPath(route, pathname);
    if (!pathParams) continue;

    const query = new URLSearchParams(search);
    const identity: Record<string, string> = {};
    for (const name of route.identity) {
      const value = pathParams[name] ?? query.get(name);
      if (!value) return null;
      identity[name] = value;
    }

    const context: Record<string, string> = {};
    const state: Record<string, string> = {};
    query.forEach((value, name) => {
      if (route.identity.includes(name)) return;
      if (route.context.includes(name)) context[name] = value;
      else state[name] = value;
    });

    return { route, identity, context, state };
  }
  return null;
}

/**
 * The key two views compare to decide whether they hold the same resource. Only
 * identity enters it, in the route's declared order, so `?portId=1&tab=a` and
 * `?tab=b&portId=1` resolve to the same key.
 */
export function detailIdentityKey(pathname: string, search = ""): string | null {
  const address = parseDetailAddress(pathname, search);
  if (!address) return null;
  const parts = address.route.identity.map(
    (name) => `${name}=${encodeURIComponent(address.identity[name])}`
  );
  return `${address.route.key}?${parts.join("&")}`;
}

type ParamValue = string | number | null | undefined;

/**
 * Builds a detail URL. Path params are filled from `identity`; every other entry of
 * `identity` and `extra` goes to the query, in that order. Null / undefined / empty
 * values are dropped, so callers can pass an optional tenant unconditionally.
 */
export function buildDetailPath(
  key: DetailRouteKey,
  identity: Record<string, ParamValue>,
  extra: Record<string, ParamValue> = {}
): string {
  const route = getDetailRoute(key);
  const used = new Set<string>();
  const path = route.path
    .split("/")
    .map((segment) => {
      const param = PATH_PARAM.exec(segment);
      if (!param) return segment;
      used.add(param[1]);
      return encodeURIComponent(String(identity[param[1]] ?? ""));
    })
    .join("/");

  const query = new URLSearchParams();
  for (const [name, value] of [...Object.entries(identity), ...Object.entries(extra)]) {
    if (used.has(name) || value === null || value === undefined || value === "") continue;
    query.set(name, String(value));
  }
  const qs = query.toString();
  return qs ? `${path}?${qs}` : path;
}

/**
 * Service type → the detail view that shows it. The FWaaS, Quotations and SD-WAN Hub
 * tables used to carry this as two hand-copied `ROUTE_INFO` maps, which had already
 * drifted (Quotations had no `sdwanInt` / `branch`).
 */
export type ServiceConnectionType =
  | "internet"
  | "cloudConnectivity"
  | "fwint"
  | "sdwanInt"
  | "port"
  | "datacenter"
  | "branch";

const SERVICE_DETAIL: Record<ServiceConnectionType, { route: DetailRouteKey; param: string }> = {
  internet: { route: "internet", param: "connectionId" },
  cloudConnectivity: { route: "cloudConnectivity", param: "connectionId" },
  // Firewall and SD-WAN interfaces are ports.
  fwint: { route: "port", param: "portId" },
  sdwanInt: { route: "port", param: "portId" },
  port: { route: "port", param: "portId" },
  datacenter: { route: "datacenter", param: "datacenterId" },
  // A branch also needs its hub's `sdwanId`, which only the SD-WAN list has; it builds
  // that address itself (see `SdwanHubPage`). This entry is the fallback other tables use.
  branch: { route: "sdwanBranch", param: "branchId" },
};

export function serviceDetailPath(type: ServiceConnectionType, id: ParamValue): string {
  const { route, param } = SERVICE_DETAIL[type];
  return buildDetailPath(route, { [param]: id });
}
